// 已成功加载的 URL
const loadedResources = new Set();

// 正在加载中的资源（用于并发去重）
const inflight = new Map();

// 节点加载状态（不污染 DOM）
const nodeLoaded = new WeakMap();

// 默认超时时间（ms）
const DEFAULT_TIMEOUT = 20000;

const _a = document.createElement('a');

/** 兼容所有浏览器的 URL 规范化 */
function normalizeUrl(url) {
  if (!url) return '';
  try {
    return new URL(url, window.location.href).href;
  } catch {
    // 旧浏览器 fallback
    _a.href = url;
    return _a.href || String(url);
  }
}

/** 获取可安全 append 的目标节点（优先 template 再 elm 再 head） */
function getAppendTarget(elm) {
  try{
    if (elm && elm.template && typeof elm.template.appendChild === 'function') return elm.template;
    if (elm && typeof elm.appendChild === 'function') return elm;
  } catch {}
  return document.head;
}

/** 查找并判断页面中是否存在已加载的 script */
function findLoadedScript(normalizedUrl) {
  for (const s of Array.from(document.scripts)) {
    if (!s.src) continue;
    try {
      if (normalizeUrl(s.src) !== normalizedUrl) continue;
    } catch {
      continue;
    }

    if (nodeLoaded.get(s)) return s;

    // 老浏览器兼容（readyState）
    const rs = s.readyState;
    if (rs === 'complete' || rs === 'loaded') {
      nodeLoaded.set(s, true);
      return s;
    }
  }
  return null;
}

/** 查找并判断页面中是否存在已加载的 stylesheet */
function findLoadedStyle(normalizedUrl) {
  const links = Array.from(document.querySelectorAll('link[rel="stylesheet"]'));
  for (const l of links) {
    if (!l.href) continue;
    try {
      if (normalizeUrl(l.href) !== normalizedUrl) continue;
    } catch {
      continue;
    }

    if (nodeLoaded.get(l)) return l;

    // 若可访问 sheet，视为已加载
    try {
      if (l.sheet) {
        nodeLoaded.set(l, true);
        return l;
      }
    } catch {
      // 抛错时不要立即标记已加载，让 loadStyle 的 onload 正常回调处理
      continue;
    }
  }
  return null;
}

/** 安全调用回调 */
function safeCallback(fn, err, url) {
  if (typeof fn === 'function') {
    try {
      fn(err, url);
    } catch (e) {
      console.error('[loadResource] callback error:', e);
    }
  }
}

/* ---------------------- loadScript ---------------------- */
export function loadScript(elm, url, callback, options = {}) {
  if (!elm) elm = document.head;
  if (!url || typeof url !== 'string') throw new TypeError('Invalid script URL');

  const timeoutMs = typeof options.timeout === 'number' ? options.timeout : DEFAULT_TIMEOUT;
  const normalizedUrl = normalizeUrl(url);

  // 已加载
  if (loadedResources.has(normalizedUrl)) {
    safeCallback(callback, null, normalizedUrl);
    return Promise.resolve(normalizedUrl);
  }

  const existing = findLoadedScript(normalizedUrl);
  if (existing) {
    loadedResources.add(normalizedUrl);
    safeCallback(callback, null, normalizedUrl);
    return Promise.resolve(normalizedUrl);
  }

  // 并发复用 inflight
  if (inflight.has(normalizedUrl)) {
    const entry = inflight.get(normalizedUrl);
    if (callback) entry.callbacks.push(callback);
    return entry.promise;
  }

  // 新建 entry
  let resolveFn, rejectFn;
  const promise = new Promise((resolve, reject) => {
    resolveFn = resolve;
    rejectFn = reject;
  });
  const entry = { promise, callbacks: callback ? [callback] : [], finished: false };
  inflight.set(normalizedUrl, entry);

  // 创建 script 元素
  const script = document.createElement('script');
  script.src = normalizedUrl;
  script.async = true;
  if (options.typeModule) script.type = 'module';
  if (options.crossOrigin) script.crossOrigin = options.crossOrigin;

  // 清理逻辑
  function cleanup(err) {
    if (entry.finished) return;
    entry.finished = true;
    inflight.delete(normalizedUrl);
    clearTimeout(timer);

    if (err) {
      try { script.remove(); } catch {}
      entry.callbacks.forEach(cb => safeCallback(cb, err, normalizedUrl));
      rejectFn(err);
    } else {
      nodeLoaded.set(script, true);
      loadedResources.add(normalizedUrl);
      entry.callbacks.forEach(cb => safeCallback(cb, null, normalizedUrl));
      resolveFn(normalizedUrl);
    }
  }

  const timer = setTimeout(() => {
    cleanup(new Error('Script load timeout: ' + normalizedUrl));
  }, timeoutMs);

  script.onload = () => cleanup(null);
  script.onerror = () => cleanup(new Error('Script load error: ' + normalizedUrl));

  const target = getAppendTarget(elm);
  try {
    target.appendChild(script);
  } catch (appendErr) {
    cleanup(new Error('Append target error: ' + appendErr.message));
  }

  return promise;
}

/* ---------------------- loadStyle ---------------------- */
export function loadStyle(elm, url, callback, options = {}) {
  if (!elm) elm = document.head;
  if (!url || typeof url !== 'string') throw new TypeError('Invalid CSS URL');

  const timeoutMs = typeof options.timeout === 'number' ? options.timeout : DEFAULT_TIMEOUT;
  const normalizedUrl = normalizeUrl(url);

  if (loadedResources.has(normalizedUrl)) {
    safeCallback(callback, null, normalizedUrl);
    return Promise.resolve(normalizedUrl);
  }

  const existing = findLoadedStyle(normalizedUrl);
  if (existing) {
    loadedResources.add(normalizedUrl);
    safeCallback(callback, null, normalizedUrl);
    return Promise.resolve(normalizedUrl);
  }

  if (inflight.has(normalizedUrl)) {
    const entry = inflight.get(normalizedUrl);
    if (callback) entry.callbacks.push(callback);
    return entry.promise;
  }

  let resolveFn, rejectFn;
  const promise = new Promise((resolve, reject) => {
    resolveFn = resolve;
    rejectFn = reject;
  });
  const entry = { promise, callbacks: callback ? [callback] : [], finished: false };
  inflight.set(normalizedUrl, entry);

  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = normalizedUrl;
  if (options.crossOrigin) link.crossOrigin = options.crossOrigin;

  function cleanup(err) {
    if (entry.finished) return;
    entry.finished = true;
    inflight.delete(normalizedUrl);
    clearTimeout(timer);

    if (err) {
      try { link.remove(); } catch {}
      entry.callbacks.forEach(cb => safeCallback(cb, err, normalizedUrl));
      rejectFn(err);
    } else {
      nodeLoaded.set(link, true);
      loadedResources.add(normalizedUrl);
      entry.callbacks.forEach(cb => safeCallback(cb, null, normalizedUrl));
      resolveFn(normalizedUrl);
    }
  }

  const timer = setTimeout(() => {
    cleanup(new Error('Style load timeout: ' + normalizedUrl));
  }, timeoutMs);

  link.onload = () => cleanup(null);
  link.onerror = () => cleanup(new Error('Style load error: ' + normalizedUrl));

  const target = getAppendTarget(elm);
  try {
    target.appendChild(link);
  } catch (appendErr) {
    cleanup(new Error('Append target error: ' + appendErr.message));
  }

  return promise;
}